import React from 'react';
import { FaCalendarAlt, FaCalendarPlus, FaPlus, FaMinus } from 'react-icons/fa';
import { useSubscriptionCart } from '../../context/SubscriptionCartContext';
import { subscriptionCartLineKey, suggestSlotIdForMenuItem } from '../../utils/subscriptionCartItems';
import {
  buildCartLineFromVariant,
  isVariableMenuItem,
} from './ourMenuUtils';

const ADD_LABEL = {
  card: ' ADD TO PLAN',
  list: ' ADD TO PLAN',
  color: ' ADD TO PLAN',
  compact: '',
  modal: ' ADD TO PLAN',
};

/**
 * Add a menu item to the subscription cart as Regular Our menu (à la carte).
 * Variable products open the details sheet unless an option is already selected.
 * Never qty-steps against a parent id.
 */
export default function MenuPlanAction({
  item,
  orderable = true,
  variant = 'card',
  className = '',
  onOpenOptions,
  selectedVariant = null,
  onAfterAdd,
}) {
  const {
    subscriptionCartItems,
    addToSubscriptionCart,
    updateSubscriptionCartQty,
  } = useSubscriptionCart();

  const variable = isVariableMenuItem(item);
  const needsOptions =
    variable && !selectedVariant && typeof onOpenOptions === 'function';

  const basePayload = selectedVariant
    ? buildCartLineFromVariant(item, selectedVariant)
    : variable
      ? null
      : {
          id: item.id,
          name: item.name,
          price: item.price,
          priceDisplay: item.priceDisplay,
          image: item.image,
          category: item.category,
        };

  const slotId = basePayload
    ? suggestSlotIdForMenuItem({ ...item, ...basePayload })
    : null;
  const payload = basePayload
    ? {
        ...basePayload,
        slotId,
        kind: 'alacarte',
      }
    : null;
  const lineKey = payload ? subscriptionCartLineKey(payload) : null;
  const planLine =
    lineKey != null
      ? (subscriptionCartItems || []).find(
          (row) => subscriptionCartLineKey(row) === lineKey,
        )
      : null;
  const quantity = Math.max(0, Number(planLine?.quantity) || 0);

  if (!orderable) return null;

  if (needsOptions) {
    return (
      <button
        type="button"
        className={`${className} menu-plan-btn`.trim()}
        onClick={() => onOpenOptions(item, { intent: 'plan' })}
        aria-label={`Add ${item.name} to plan — choose an option`}
        title="Add to meal plan — choose an option"
      >
        <FaCalendarPlus />
        {ADD_LABEL[variant] ?? ADD_LABEL.card}
      </button>
    );
  }

  if (!payload) return null;

  if (quantity > 0) {
    return (
      <div
        className={`menu-qty-stepper menu-qty-stepper--plan menu-qty-stepper--${variant}`}
        role="group"
        aria-label={`Meal plan quantity for ${item.name}`}
        title="Meal plan"
      >
        <button
          type="button"
          className="menu-qty-stepper__btn"
          onClick={() => updateSubscriptionCartQty(payload.id, slotId, quantity - 1)}
          aria-label={`Decrease meal plan quantity of ${item.name}`}
        >
          <FaMinus />
        </button>
        <span className="menu-qty-stepper__value" aria-live="polite">
          <span className="menu-qty-stepper__kind">
            <FaCalendarAlt aria-hidden />
            <span className="menu-qty-stepper__kind-text">Plan</span>
          </span>
          <span className="menu-qty-stepper__count">{quantity}</span>
        </span>
        <button
          type="button"
          className="menu-qty-stepper__btn"
          onClick={() => updateSubscriptionCartQty(payload.id, slotId, quantity + 1)}
          aria-label={`Increase meal plan quantity of ${item.name}`}
        >
          <FaPlus />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${className} menu-plan-btn`.trim()}
      onClick={() => {
        addToSubscriptionCart(payload);
        if (typeof onAfterAdd === 'function') onAfterAdd();
      }}
      aria-label={`Add ${item.name} to subscription cart`}
      title="Add to subscription as Our menu"
    >
      <FaCalendarPlus />
      {ADD_LABEL[variant] ?? ADD_LABEL.card}
    </button>
  );
}
